import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SourcingEventStatus, useGetSourcingEventQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";
import { ChangeResponsiblePersonButton } from "./ChangeResponsiblePersonButton";
import { DraftNoEditAccessBanner } from "./DraftNoEditAccessBanner";
import { EventHeader } from "./EventHeader";
import { PublicationStatusBanner } from "./PublicationStatusBanner";
import { EventPhaseTimeline } from "./timeline/EventPhaseTimeline";
import { EventTabs, parseEventTab, type EventTab } from "./EventTabs";
import { RoundSelectionProvider } from "./rounds/RoundSelectionContext";

const PENDING_POLL_MS = 15000;

export const SourcingEventView = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = useSub();
  const { selectedOrg } = useLoggedInWithOrgContextState();

  const [pollInterval, setPollInterval] = useState(0);
  const { data: event, isLoading } = useGetSourcingEventQuery(
    eventId ? { eventId } : skipToken,
    { pollingInterval: pollInterval },
  );
  useEffect(() => {
    setPollInterval(event?.status === SourcingEventStatus.PublicationPending ? PENDING_POLL_MS : 0);
  }, [event?.status]);

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  // Gate on missing data, not isError — a transient poll failure must not blank the page.
  if (!event) {
    return (
      <Center py="20">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
      </Center>
    );
  }

  const isDraft = event.status === SourcingEventStatus.Draft;
  if (isDraft && sub.canCreateEvents) {
    return <Navigate to={urls.publicEvents.edit.go(event.id).info} replace />;
  }

  const activeTab = parseEventTab(searchParams.get("tab"));
  const onTabChange = (tab: EventTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const onViewPublic =
    event.eventContext.type === "Public" ? () => navigate(urls.portal.view.go(event.id)) : undefined;

  return (
    <RoundSelectionProvider event={event}>
      <Box>
        <EventHeader
          event={event}
          onViewPublic={onViewPublic}
          responsibleEditControl={
            event.responsiblePerson && (
              // The buyer view is always scoped to the owning org (the BFF rejects access from other
              // orgs), so the current org is the event owner — the roster to pick a new responsible from.
              <ChangeResponsiblePersonButton
                eventId={event.id}
                ownerOrgId={selectedOrg.id}
                currentResponsible={event.responsiblePerson}
              />
            )
          }
        />
        {isDraft && <DraftNoEditAccessBanner />}
        <PublicationStatusBanner status={event.status} />
        <EventPhaseTimeline event={event} />
        <Box mt="6">
          <EventTabs event={event} activeTab={activeTab} onTabChange={onTabChange} />
        </Box>
      </Box>
    </RoundSelectionProvider>
  );
};
